/**
 * Game Type Descriptors for Poker Reborn
 *
 * Centralizes all game-type-specific configuration in one place.
 * Each game type (Cash, Fast-Fold, SNG) has a descriptor that drives
 * both the lobby UI and the Custom Play wizard content.
 *
 * @see docs/screens/lobby/GAME_TYPE_DESCRIPTORS.md - Feature documentation
 * @see src/config/managers.ts - Manager data (auto-generated)
 * @see src/config/lobbyCards.ts - Lobby card descriptors
 */

import type { GameType } from '@/config/managers';
import type { WizardContent } from '@/components/lobby/wizard/WizardContent';
import { CashWizardContent } from '@/components/lobby/wizard/CashWizardContent';
import { FastFoldWizardContent } from '@/components/lobby/wizard/FastFoldWizardContent';
import { SngWizardContent } from '@/components/lobby/wizard/SngWizardContent';

// =============================================================================
// TYPES
// =============================================================================

export interface GameTypeDescriptor {
  /** The game type key */
  readonly gameType: GameType;

  /** Display title for wizard popup (e.g., "Cash Games") */
  readonly title: string;

  /** Short subtitle for lobby cards (e.g., "No-Limit Hold'em") */
  readonly subtitle: string;

  /** Default seat count for this game type */
  readonly defaultSeats: 6 | 9;

  /** Whether the player can choose seats in the wizard */
  readonly seatSelectable: boolean;

  /** Create the wizard content component for this game type */
  createWizardContent(contentWidth: number): WizardContent;
}

// =============================================================================
// DESCRIPTORS
// =============================================================================

export const GAME_TYPE_DESCRIPTORS: Record<GameType, GameTypeDescriptor> = {
  cash: {
    gameType: 'cash',
    title: 'Cash Games',
    subtitle: "No-Limit Hold'em",
    defaultSeats: 6,
    seatSelectable: true,
    createWizardContent: (contentWidth: number) =>
      new CashWizardContent(contentWidth),
  },

  fastfold: {
    gameType: 'fastfold',
    title: 'Fast-Fold',
    subtitle: 'Speed Poker',
    defaultSeats: 6,
    seatSelectable: false,
    createWizardContent: (contentWidth: number) =>
      new FastFoldWizardContent(contentWidth),
  },

  sng: {
    gameType: 'sng',
    title: 'Sit & Go',
    subtitle: 'Tournament',
    defaultSeats: 9,
    seatSelectable: false,
    createWizardContent: (contentWidth: number) =>
      new SngWizardContent(contentWidth),
  },
};

/**
 * Get the descriptor for a game type.
 * @param gameType - The game type to look up
 */
export function getGameTypeDescriptor(gameType: GameType): GameTypeDescriptor {
  return GAME_TYPE_DESCRIPTORS[gameType];
}
